/**
 * Beginner mode teaching aids: example dice for every Kill Quest (the roll that destroys a
 * card) and every power, whether an enemy's
 * held dice are working toward your card, and which of your dice are worth keeping (HINT).
 */
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  MAX_SEQUENCES,
  OddsEngine,
  applyMods,
  hasText,
  killOdds,
  t,
  type CardDef,
  type CardId,
  type GameView,
  type TurnView,
} from '@kadice/shared';
import { killerName, powerText, shortPowerOf } from './cards';
import { Dice } from './components/Dice';

interface Example {
  dice: number[];
  /** Indexes of the dice that make the combination. */
  lit: number[];
}

const ALL = [0, 1, 2, 3, 4];

const KILL_EXAMPLES: Record<string, Example> = {
  xxyy: { dice: [2, 2, 5, 5, 1], lit: [0, 1, 2, 3] },
  xxx: { dice: [4, 4, 4, 1, 6], lit: [0, 1, 2] },
  xxxx: { dice: [6, 6, 6, 6, 2], lit: [0, 1, 2, 3] },
  xxxyy: { dice: [3, 3, 3, 5, 5], lit: ALL },
  seq4: { dice: [1, 2, 3, 4, 6], lit: [0, 1, 2, 3] },
  seq5: { dice: [2, 3, 4, 5, 6], lit: ALL },
  e: { dice: [2, 4, 6, 4, 2], lit: ALL },
  o: { dice: [1, 3, 5, 5, 3], lit: ALL },
  leq10: { dice: [1, 2, 2, 3, 1], lit: ALL },
  geq25: { dice: [6, 6, 5, 4, 5], lit: ALL },
  kadice: { dice: [1, 2, 3, 5, 6], lit: ALL },
};

/** A roll that destroys a card with this Kill Quest. */
export function killExample(killer: string): Example | null {
  const pair = /^p([1-6])$/.exec(killer);
  if (pair) {
    const n = Number(pair[1]);
    const others = [1, 2, 3, 4, 5, 6].filter((f) => f !== n).slice(0, 3);
    return { dice: [n, n, ...others], lit: [0, 1] };
  }
  return KILL_EXAMPLES[killer] ?? null;
}

const sum = (dice: readonly number[]) => dice.reduce((a, b) => a + b, 0);

interface PowerExample {
  before: number[];
  /** The die the power changes, and what it becomes (none for powers that don't change dice). */
  change?: { index: number; to: number };
  /** The dice the power needs (shown lit when it changes none; otherwise only the changed die is lit). */
  lit: number[];
  caption: string;
}

/** A roll showing this power at work, or null for powers that don't involve dice. */
export function powerExample(power: string): PowerExample | null {
  const wild = /^w([1-6])$/.exec(power);
  if (wild) {
    const n = Number(wild[1]);
    const a = n === 5 ? 4 : 5;
    const rest = [1, 2, 3, 6].filter((f) => f !== n && f !== a).slice(0, 2);
    return {
      before: [n, a, a, ...rest],
      change: { index: 0, to: a },
      lit: [0, 1, 2],
      caption: t('example.power.wild', { n, a }),
    };
  }
  switch (power) {
    case 'xxx':
      return {
        before: [4, 4, 5, 1, 6],
        change: { index: 2, to: 4 },
        lit: [0, 1, 2],
        caption: t('example.power.xxx'),
      };
    case 'leq10': {
      const before = [1, 2, 2, 3, 3];
      return {
        before,
        change: { index: 4, to: 2 },
        lit: ALL,
        caption: t('example.power.leq10', { total: sum(before) }),
      };
    }
    case 'geq25': {
      const before = [6, 6, 5, 4, 3];
      return {
        before,
        change: { index: 4, to: 4 },
        lit: ALL,
        caption: t('example.power.geq25', { total: sum(before) }),
      };
    }
    case 'xxxx':
      return {
        before: [5, 5, 5, 5, 2],
        lit: [0, 1, 2, 3],
        caption: t('example.power.xxxx'),
      };
    case 'seq5':
      return {
        before: [2, 3, 4, 5, 6],
        lit: ALL,
        caption: t('example.power.seq5'),
      };
    case 'e':
      return {
        before: [2, 4, 6, 4, 2],
        lit: ALL,
        caption: t('example.power.e'),
      };
    case 'o':
      return {
        before: [1, 3, 5, 5, 3],
        lit: ALL,
        caption: t('example.power.o'),
      };
    case 'kadice':
      return { before: [1, 2, 3, 5, 6], lit: ALL, caption: t('example.power.kadice') };
    default:
      return null;
  }
}

const pct = (p: number) => t('format.percent', { n: Math.round(p * 100) });

/** Chance to roll this card's Kill Quest in one roll from scratch, without powers. */
export const rollChance = (card: CardDef) => pct(killOdds(card.killer, null, 1));

/** Are these held dice working toward this Kill Quest (better odds next roll than holding nothing)? */
export function heldTargets(killer: string, power: string | null, held: readonly number[]): boolean {
  if (held.length === 0) return false;
  return killOdds(killer, power, 1, held) > killOdds(killer, power, 1) + 0.001;
}

/** Odds engines for the hint, by enemy Kill Quests and your power (their memo tables make repeat hints cheap). */
const hintEngines = new Map<string, OddsEngine>();

/**
 * The HINT button: the dice worth keeping now, as pool indexes — what the bots' odds engine
 * (every enemy card worth the same) would do. To attack: the fewest dice that still make every
 * kill (only kept dice count). Otherwise: the dice to hold for the next roll. Before the roll:
 * the saved dice worth keeping.
 */
export function usefulDice(
  view: GameView,
  catalog: ReadonlyMap<CardId, CardDef>,
  turn: TurnView,
  power: string | null,
): number[] {
  const targets = view.players.flatMap((p) => {
    const def = p.id !== view.you.id && p.card !== null ? catalog.get(p.card) : undefined;
    return def ? [{ id: p.id, killer: def.killer, value: 1 }] : [];
  });
  if (targets.length === 0) return [];
  const key = `${targets
    .map((x) => x.killer)
    .sort()
    .join()}|${power ?? ''}`;
  let engine = hintEngines.get(key);
  if (!engine) {
    if (hintEngines.size > 20) hintEngines.clear();
    hintEngines.set(key, (engine = new OddsEngine({ targets, power, rho: 1 })));
  }
  // Rolls still to come after this one (as the bots count them): later passes, plus Small Straight's extra turn.
  const extra = power === 'seq4' && view.sequence === 1 && !turn.extra && !turn.bonus ? 1 : 0;
  const rollsAfter = turn.bonus ? 0 : Math.max(0, MAX_SEQUENCES - view.sequence) + extra;

  const subsets = (n: number) =>
    Array.from({ length: 1 << n }, (_, mask) => Array.from({ length: n }, (_, i) => i).filter((i) => (mask >> i) & 1));
  if (!turn.hasRolled) {
    const saved = turn.bonus ? [] : turn.pool;
    let best = { keep: [] as number[], value: -1 };
    for (const keep of subsets(saved.length)) {
      const value = engine.value(
        rollsAfter + 1,
        keep.map((i) => saved[i]!),
      );
      if (value > best.value + 1e-9 || (Math.abs(value - best.value) <= 1e-9 && keep.length > best.keep.length)) {
        best = { keep, value };
      }
    }
    return best.value > 0 ? best.keep : [];
  }
  const act = engine.decide(turn.pool, turn.pool.length - turn.rolled, rollsAfter);
  if (act.action !== 'attack') return act.keep;
  const dice = applyMods(turn.pool, act.mods);
  const smallest = subsets(dice.length)
    .sort((a, b) => a.length - b.length)
    .find((keep) => engine.payoff(keep.map((i) => dice[i]!)).value >= act.value - 1e-9);
  return smallest ?? act.keep;
}

/** Example dice for a card's Kill Quest, with the dice that make it lifted. */
export function KillExample({ card }: { card: CardDef }) {
  const ex = killExample(card.killer);
  return ex ? <Dice values={ex.dice} small lifted={ex.lit} /> : null;
}

/** A dice-changing example's loop (ms): the roll, its ▲/▼ shows, is pressed, the die changes, then again. */
const EX_ARROW_MS = 1_000;
const EX_PRESS_MS = 2_200;
const EX_CHANGE_MS = 2_500;
const EX_LOOP_MS = 5_500;

/** Ms into the example's loop, ticking while mounted. */
function useLoop(on: boolean) {
  const [at, setAt] = useState(0);
  useEffect(() => {
    if (!on) return;
    const start = performance.now();
    const timer = setInterval(() => setAt((performance.now() - start) % EX_LOOP_MS), 100);
    return () => clearInterval(timer);
  }, [on]);
  return at;
}

/**
 * Example dice for a power. Dice-changing powers play in one row, on a loop: the Original Roll,
 * then the changed die's ▲ or ▼ (as on the hold mat) shows and is pressed, then the die changes
 * and the label turns to After Changes. Other powers: the roll, with the dice it needs lit.
 */
export function PowerExampleDice({ power }: { power: string }) {
  const ex = powerExample(power);
  const at = useLoop(!!ex?.change);
  if (!ex) return null;
  const change = ex.change;
  const dir = change && change.to > ex.before[change.index]! ? 'up' : 'down';
  const changed = at >= EX_CHANGE_MS;
  return (
    <div className="example">
      <div className="example-dice">
        {change ? (
          <figure className={`example-step ${dir}${changed ? ' after' : ''}`}>
            <Dice
              values={changed ? ex.before.map((v, i) => (i === change.index ? change.to : v)) : ex.before}
              small
              lifted={[change.index]}
              nudge={{
                index: change.index,
                dir: at >= EX_ARROW_MS && !changed ? dir : null,
                pressed: at >= EX_PRESS_MS,
              }}
            />
            <figcaption>{t(changed ? 'example.after' : 'example.before')}</figcaption>
          </figure>
        ) : (
          <Dice values={ex.before} small lifted={ex.lit} />
        )}
      </div>
      <span className="example-caption">{ex.caption}</span>
    </div>
  );
}

/** A power's example: dice when it changes or needs dice, otherwise just the words (Two Pair, Full House, Small Straight). */
export function PowerExampleAny({ power }: { power: string }) {
  if (powerExample(power)) return <PowerExampleDice power={power} />;
  return hasText(`example.power.${power}`) ? <span className="muted small">{t(`example.power.${power}`)}</span> : null;
}

/**
 * A card's power in short, with a ? that opens the full text and its example (turn panel, card
 * intro). `before`: shown ahead of the short text (the turn panel's 'Power:').
 */
export function PowerLine({ card, className, before }: { card: CardDef; className: string; before?: string }) {
  return (
    <p className={className}>
      {before && <span>{before}</span>} <span className="power-short">{shortPowerOf(card.power)}</span>
      <PowerHelp card={card} />
    </p>
  );
}

/** The ? that opens a card's power in full, with its example. `className`: extra classes (placement). */
export function PowerHelp({ card, className }: { card: CardDef; className?: string }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        className={`power-help${className ? ` ${className}` : ''}`}
        onClick={(e) => {
          e.stopPropagation();
          setOpen(true);
        }}
        title={t('turn.powerMore')}
        aria-label={t('turn.powerMore')}
      >
        ?
      </button>
      {open &&
        // On the body: a card intro box is a containing block, which would trap a fixed backdrop.
        createPortal(
          // Clicks stop here: React bubbles them out of the portal to the card or seat under the ?.
          <div
            className="modal-backdrop power-help-backdrop"
            onClick={(e) => {
              e.stopPropagation();
              setOpen(false);
            }}
          >
            <div className="modal power-modal" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
              <h3>{t('lesson.power')}</h3>
              <p>{powerText(card)}</p>
              <PowerExampleAny power={card.power} />
              <div className="row actions">
                <button className="primary" onClick={() => setOpen(false)}>
                  {t('common.close')}
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}

/** Kill Quest and power of a card, each with example dice (enlarged card view, card intro). */
export function CardLesson({ card }: { card: CardDef }) {
  return (
    <div className="card-lesson">
      <section>
        <h3>{t('lesson.quest', { killer: killerName(card) })}</h3>
        <KillExample card={card} />
        <span className="muted small">{t('lesson.questHint', { chance: rollChance(card) })}</span>
      </section>
      <section>
        <h3>{t('lesson.power')}</h3>
        <span>{powerText(card)}</span>
        <PowerExampleDice power={card.power} />
      </section>
    </div>
  );
}
